import React, { useState, useEffect } from 'react';
import { shipmentService, SHIPMENT_STATUS_LABEL } from '../api/shipmentService';
import Pagination from '../components/Common/Pagination';
import '../components/Common/Common.css';

function StatusPill({ status }) {
  const cfg = SHIPMENT_STATUS_LABEL[status] || { label: status, color: '#444', bg: '#eee' };
  return (
    <span style={{
      fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 99,
      background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap', display: 'inline-block',
    }}>
      {cfg.label}
    </span>
  );
}

function Shipments() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [refreshingId, setRefreshingId] = useState(null);
  const [pingStatus, setPingStatus] = useState(null);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, currentPage]);

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await shipmentService.list({
        page: currentPage,
        status: statusFilter || undefined,
        search: search || undefined,
        per_page: 25,
      });
      setItems(data.data || []);
      setLastPage(data.last_page || 1);
    } catch (e) {
      setError(e.response?.data?.message || 'Échec du chargement.');
    } finally {
      setLoading(false);
    }
  };

  const handlePing = async () => {
    setPingStatus({ loading: true });
    try {
      const r = await shipmentService.ping();
      setPingStatus({ ok: r.ok, msg: r.ok ? `Token Sendit OK (${r.token_preview})` : r.message });
    } catch (e) {
      setPingStatus({ ok: false, msg: e.response?.data?.message || 'Échec du ping' });
    }
  };

  const handleRefresh = async (id) => {
    setRefreshingId(id);
    setError('');
    setInfo('');
    try {
      const resp = await shipmentService.refresh(id);
      setInfo(resp.changed ? `Livraison #${id} : statut mis à jour.` : `Livraison #${id} : aucun changement.`);
      setItems((prev) => prev.map((s) => (s.id === id ? resp.shipment : s)));
    } catch (e) {
      setError(e.response?.data?.message || 'Échec du rafraîchissement.');
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1>Livraisons (Sendit)</h1>
        <button type="button" className="btn btn-secondary" onClick={handlePing}>
          Tester la connexion Sendit
        </button>
      </div>

      {pingStatus && (
        <div
          style={{ marginBottom: 16, padding: 12, borderRadius: 6,
            background: pingStatus.ok ? '#d4edda' : (pingStatus.loading ? '#e2e3e5' : '#f8d7da'),
            color:      pingStatus.ok ? '#155724' : (pingStatus.loading ? '#383d41' : '#721c24') }}
        >
          {pingStatus.loading ? 'Test en cours…' : pingStatus.msg}
        </div>
      )}

      {error && <div style={{ marginBottom: 16, padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6 }}>{error}</div>}
      {info && <div style={{ marginBottom: 16, padding: 12, background: '#d4edda', color: '#155724', borderRadius: 6 }}>{info}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Rechercher (code de suivi, n° facture, nom, téléphone)…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            style={{ flex: '1 1 240px', minWidth: 200, padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6 }}
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            style={{ padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6 }}
          >
            <option value="">Tous les statuts</option>
            {Object.entries(SHIPMENT_STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : items.length === 0 ? (
        <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 40, color: '#666' }}>
          Aucune livraison.
        </div></div>
      ) : (
        <div className="card">
          <div className="card-body" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left', fontSize: 13 }}>
                  <th style={{ padding: '10px 8px' }}>Code</th>
                  <th style={{ padding: '10px 8px' }}>Commande</th>
                  <th style={{ padding: '10px 8px' }}>Client</th>
                  <th style={{ padding: '10px 8px' }}>Statut</th>
                  <th style={{ padding: '10px 8px' }}>COD</th>
                  <th style={{ padding: '10px 8px' }}>Dernier sync</th>
                  <th style={{ padding: '10px 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => {
                  const order = s.order || {};
                  const clientName = order.client
                    ? `${order.client.first_name || ''} ${order.client.last_name || ''}`.trim() || order.client.email
                    : order.guest_name;
                  return (
                    <tr key={s.id} style={{ borderBottom: '1px solid #f1f3f5', fontSize: 13 }}>
                      <td style={{ padding: '10px 8px', fontFamily: 'monospace' }}>{s.provider_code || '—'}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <div style={{ fontWeight: 600 }}>#{order.id}</div>
                        <div style={{ color: '#666', fontSize: 11 }}>{order.invoice_number || ''}</div>
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <div>{clientName || '—'}</div>
                        <div style={{ color: '#666', fontSize: 11 }}>{order.guest_phone || order.client?.phone || ''}</div>
                      </td>
                      <td style={{ padding: '10px 8px' }}><StatusPill status={s.status} /></td>
                      <td style={{ padding: '10px 8px' }}>{parseFloat(s.cod_amount || 0).toFixed(2)} DH</td>
                      <td style={{ padding: '10px 8px', color: '#666' }}>
                        {s.last_synced_at ? new Date(s.last_synced_at).toLocaleString('fr-FR') : '—'}
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <button
                          type="button"
                          onClick={() => handleRefresh(s.id)}
                          disabled={refreshingId === s.id}
                          style={{ padding: '4px 10px', border: '1px solid #ced4da', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                        >
                          {refreshingId === s.id ? '...' : 'Sync'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination currentPage={currentPage} lastPage={lastPage} onPageChange={setCurrentPage} />
    </div>
  );
}

export default Shipments;
